import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import UnblockGrid from "./components/UnblockGrid";
import Block from "./components/Block";
import PrisonerBlock from "./components/PrisonerBlock";
import GameControls from "./components/GameControls";
import LevelComplete from "./components/LevelComplete";
import { useUnblockGame } from "./hooks/useUnblockGame";
import { alreadyPlayed, getLeaderboard, insertScore, updateReport, updateScore } from "../../api/scoreApi";
import { sendReport } from "../../functions/sendReport";
import loaderIcon from "../../img/icons.png";
import "./game.css";

/** Slightly longer than `.unblock-block--escaping` animation (0.6s) so overlay appears after exit. */
const LEVEL_COMPLETE_OVERLAY_DELAY_MS = 700;

function Game({ updateHeader }) {
  const navigate = useNavigate();
  const levelStartTimeRef = useRef(Date.now());
  // Track "active play" time (pauses when tab hidden / window blurred)
  const levelActiveMsRef = useRef(0);
  const lastActiveTickRef = useRef(Date.now());
  const isPlayActiveRef = useRef(true);
  const pauseStartedAtRef = useRef(null);
  const cumulativeTimeSecondsRef = useRef(0);
  const didInsertScoreRef = useRef(false);
  const lastReportedLevelRef = useRef(0);
  const reportIdRef = useRef(null);
  const demoLimitPromptShownRef = useRef(false);
  const [showLevelCompleteOverlay, setShowLevelCompleteOverlay] = useState(false);
  const [levelStartTimestamp, setLevelStartTimestamp] = useState(Date.now());
  const [isNextLevelLoading, setIsNextLevelLoading] = useState(false);

  const {
    blocks,
    moveCount,
    isComplete,
    currentLevel,
    optimalMoves,
    canUndo,
    moveBlockToPosition,
    getBlockMoveRange,
    undo,
    restart,
    nextLevel,
    goToLevel,
  } = useUnblockGame();
  
  useEffect(() => {
    updateHeader({
      backButtonRedirect: "/rules",
    });
    // updateHeader is recreated in parent on each render; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const prisonerBlock = useMemo(() => 
    blocks.find(b => b.isPrisoner), 
    [blocks]
  );
  
  const prisonerRow = prisonerBlock ? prisonerBlock.y : 2;
  
  const regularBlocks = useMemo(() => 
    blocks.filter(b => !b.isPrisoner),
    [blocks]
  );
  
  const handleCancel = () => {
    navigate("/intro");
  };

  const flushActiveTime = useCallback(() => {
    const now = Date.now();
    if (isPlayActiveRef.current) {
      const delta = Math.max(0, now - lastActiveTickRef.current);
      levelActiveMsRef.current += delta;
    }
    lastActiveTickRef.current = now;
  }, []);

  const formatElapsedTime = useCallback((elapsedMs) => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  }, []);

  const parseTimeToSeconds = useCallback((value) => {
    if (typeof value !== "string") return 0;
    const parts = value
      .split(":")
      .map((part) => Number.parseInt(part, 10))
      .filter((part) => Number.isFinite(part));

    if (parts.length === 2) {
      const [minutes, seconds] = parts;
      return Math.max(0, (minutes * 60) + seconds);
    }

    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts;
      return Math.max(0, (hours * 3600) + (minutes * 60) + seconds);
    }

    return 0;
  }, []);

  const formatSecondsToTime = useCallback((totalSeconds) => {
    const normalized = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(normalized / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (normalized % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  }, []);

  const getApiUserData = useCallback(() => {
    try {
      const sessionRaw = sessionStorage.getItem("userData");
      const localRaw = localStorage.getItem("userData");
      const raw = sessionRaw || localRaw;
      // console.log("getApiUserData raw", raw);
      if (!raw || raw === "null" || raw === "undefined") return null;

      const parsed = JSON.parse(raw);
      const data = parsed?.data && typeof parsed.data === "object" ? parsed.data : parsed || {};
      // console.log("getApiUserData data", data);
      const normalized = {
        ...data,
        userId: data.userId ?? data.user_id ?? data.uid ?? data.id ?? data.user?.id ?? null,
        sessionId: data.sessionId ?? data.session_id ?? data.sid ?? null,
        organizationId:
          data.organizationId ?? data.organization_id ?? data.orgId ?? data.organization?.id ?? null,
        roles: data.roles ?? data.role ?? data.user_type ?? null,
      };
      return normalized;
    } catch (error) {
      console.error("Failed to parse userData from session:", error);
      return null;
    }
  }, []);

  const updateScoreWithRole = useCallback(async (userData, totalPoints, time = "00:00") => {
    const persistUserDataPatch = (patch) => {
      const patchStorage = (storage) => {
        try {
          const raw = storage.getItem("userData");
          if (!raw || raw === "null" || raw === "undefined") return;
          const parsed = JSON.parse(raw);
          if (parsed?.data && typeof parsed.data === "object") {
            const updated = { ...parsed, data: { ...parsed.data, ...patch } };
            storage.setItem("userData", JSON.stringify(updated));
            return;
          }
          if (parsed && typeof parsed === "object") {
            const updated = { ...parsed, ...patch };
            storage.setItem("userData", JSON.stringify(updated));
          }
        } catch (error) {
          console.warn("Failed to persist userData patch:", error);
        }
      };

      patchStorage(sessionStorage);
      patchStorage(localStorage);
    };

    const reportdata = {
      gameId: userData.gameId,
      name: userData.name,
      sessionId: userData.sessionId,
      userId: userData.userId,
      organizationId: userData.organizationId,
      points: totalPoints,
      reportId: reportIdRef.current || userData.reportId || userData.report_id || null,
      time,
      token: userData.token || "",
      role: userData.roles || userData.user_type || userData.role || "ORG_USER",
      ans: ""
    };
    console.log("reportdata", reportdata);

    try {
      const reportResponse = await sendReport(reportdata);
      const returnedReportId =
        reportResponse?.data?.reportId ??
        reportResponse?.data?.report_id ??
        reportResponse?.data?.id ??
        reportResponse?.reportId ??
        reportResponse?.report_id ??
        reportIdRef.current ??
        userData.reportId ??
        userData.report_id ??
        null;
      reportIdRef.current = returnedReportId;
      if (returnedReportId) {
        userData.reportId = returnedReportId;
        userData.report_id = returnedReportId;
        persistUserDataPatch({ reportId: returnedReportId, report_id: returnedReportId });
      }
      const returnedUserId =
        reportResponse?.data?.userId ??
        reportResponse?.data?.user_id ??
        reportResponse?.userId ??
        reportResponse?.user_id ??
        userData.userId ??
        null;

      if (returnedUserId && returnedUserId !== userData.userId) {
        userData.userId = returnedUserId;
        persistUserDataPatch({ userId: returnedUserId, user_id: returnedUserId });
      }

      const effectiveReportId = reportIdRef.current || null;

      if (effectiveReportId) {
        await updateReport({
          userId: userData.userId,
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
          reportId: effectiveReportId,
        });
      }
      return {
        reportId: effectiveReportId,
        userId: userData.userId,
      };
    } catch (error) {
      console.error("Failed to update score with role:", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    const apiUserData = getApiUserData();
    if (!apiUserData) return;
    reportIdRef.current = apiUserData.reportId ?? apiUserData.report_id ?? null;
  }, [getApiUserData]);

  useEffect(() => {
    const loadPlayedState = async () => {
      const apiUserData = getApiUserData();
      if (!apiUserData?.userId || !apiUserData?.sessionId || !apiUserData?.organizationId) {
        console.warn("alreadyPlayed skipped: missing identifiers", apiUserData);
        await goToLevel(1);
        return;
      }

      try {
        const playedRes = await alreadyPlayed({
          userId: apiUserData.userId,
          sessionId: apiUserData.sessionId,
          organizationId: apiUserData.organizationId,
        });

        const existingReportId =
          playedRes?.reportId ??
          playedRes?.report_id ??
          playedRes?.data?.reportId ??
          playedRes?.data?.report_id ??
          playedRes?.data?.id ??
          null;

        if (existingReportId) {
          reportIdRef.current = existingReportId;
          apiUserData.reportId = existingReportId;
          apiUserData.report_id = existingReportId;

          const persistReportId = (storage) => {
            try {
              const raw = storage.getItem("userData");
              if (!raw || raw === "null" || raw === "undefined") return;
              const parsed = JSON.parse(raw);
              if (parsed?.data && typeof parsed.data === "object") {
                const updated = {
                  ...parsed,
                  data: {
                    ...parsed.data,
                    reportId: existingReportId,
                    report_id: existingReportId,
                  },
                };
                storage.setItem("userData", JSON.stringify(updated));
                return;
              }
              if (parsed && typeof parsed === "object") {
                const updated = {
                  ...parsed,
                  reportId: existingReportId,
                  report_id: existingReportId,
                };
                storage.setItem("userData", JSON.stringify(updated));
              }
            } catch (error) {
              console.warn("Failed to persist reportId from alreadyPlayed:", error);
            }
          };

          persistReportId(sessionStorage);
          persistReportId(localStorage);
        }

        const priorScore = Number(
          playedRes?.score ??
          playedRes?.data?.score ??
          0
        );
        const priorTimeRaw = playedRes?.time ?? playedRes?.data?.time ?? "00:00";
        cumulativeTimeSecondsRef.current = parseTimeToSeconds(priorTimeRaw);

        if (playedRes?.status === "success" && Number.isFinite(priorScore) && priorScore > 0) {
          await goToLevel(priorScore);
          didInsertScoreRef.current = true;
          lastReportedLevelRef.current = Math.max(0, priorScore - 1);
        } else {
          cumulativeTimeSecondsRef.current = 0;
          await goToLevel(1);
        }
      } catch (error) {
        console.warn("alreadyPlayed check failed, starting from level 1:", error);
        cumulativeTimeSecondsRef.current = 0;
        await goToLevel(1);
      }
    };

    const userData = getApiUserData();
    loadPlayedState();
    console.log("reportIdRef.userData----------------", userData ? { ...userData } : userData);
  }, [getApiUserData, goToLevel, parseTimeToSeconds]);

  useEffect(() => {
    if (!isComplete) {
      setShowLevelCompleteOverlay(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowLevelCompleteOverlay(true);
    }, LEVEL_COMPLETE_OVERLAY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isComplete]);

  useEffect(() => {
    const now = Date.now();
    levelStartTimeRef.current = now;
    levelActiveMsRef.current = 0;
    lastActiveTickRef.current = now;
    pauseStartedAtRef.current = null;
    setLevelStartTimestamp(now);
  }, [currentLevel]);

  // Pause timer when tab is hidden / window loses focus (prevents idle time inflation).
  useEffect(() => {
    const recomputeActiveFlag = () => {
      flushActiveTime();
      const visible = document.visibilityState === "visible";
      const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
      const shouldBeActive = visible && focused && !isComplete;

      // Keep the UI timer (Date.now - levelStartTimestamp) aligned with active time
      // by shifting its start forward for the duration of pauses.
      if (!shouldBeActive) {
        if (pauseStartedAtRef.current == null) {
          pauseStartedAtRef.current = Date.now();
        }
      } else if (pauseStartedAtRef.current != null) {
        const pausedMs = Math.max(0, Date.now() - pauseStartedAtRef.current);
        pauseStartedAtRef.current = null;
        setLevelStartTimestamp((prev) => prev + pausedMs);
      }

      isPlayActiveRef.current = shouldBeActive;
    };

    // initialize
    recomputeActiveFlag();

    document.addEventListener("visibilitychange", recomputeActiveFlag);
    window.addEventListener("focus", recomputeActiveFlag);
    window.addEventListener("blur", recomputeActiveFlag);

    return () => {
      document.removeEventListener("visibilitychange", recomputeActiveFlag);
      window.removeEventListener("focus", recomputeActiveFlag);
      window.removeEventListener("blur", recomputeActiveFlag);
    };
  }, [flushActiveTime, isComplete]);

  useEffect(() => {
    if (!isComplete) return;
    if (currentLevel < 2) return;
    if (demoLimitPromptShownRef.current) return;

    const user = getApiUserData();
    const sessionId = String(user?.sessionId ?? "").trim().toLowerCase();
    if (!sessionId.startsWith("demo")) return;

    demoLimitPromptShownRef.current = true;
    Swal.fire({
      icon: "warning",
      title: "Thank you for Playing😉",
      text: "Subscribe to any PLAN to play with your peers.",
      confirmButtonText: "OK",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.assign(process.env.REACT_APP_BASE_URL);
      }
    });
  }, [currentLevel, getApiUserData, isComplete]);

  useEffect(() => {

    if (!isComplete) return;
    if (currentLevel <= lastReportedLevelRef.current) return;

    const syncLevelScore = async () => {
      // alert("isComplete: " + isComplete);
      const apiUserData = getApiUserData();
      // console.log("apiUserData", apiUserData);
      // console.log("isComplete", isComplete);
      // console.log("currentLevel", currentLevel);
      // console.log("lastReportedLevelRef.current", lastReportedLevelRef.current);
      if (!apiUserData?.userId || !apiUserData?.sessionId || !apiUserData?.organizationId) {
        console.warn("Score sync skipped: missing identifiers", apiUserData);
        return;
      }

      flushActiveTime();
      const elapsedMs = levelActiveMsRef.current;
      const levelElapsedSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
      const cumulativeSeconds = cumulativeTimeSecondsRef.current + levelElapsedSeconds;
      const formattedTime = formatSecondsToTime(cumulativeSeconds);
      const nextScore = currentLevel;

      try {
        const isFirstCompletion = !didInsertScoreRef.current;
        console.log("Score sync start", {
          level: currentLevel,
          mode: isFirstCompletion ? "insert-first-then-report" : "report-first-then-update",
          userId: apiUserData.userId,
          sessionId: apiUserData.sessionId,
          organizationId: apiUserData.organizationId,
        });
        if (isFirstCompletion) {
          await insertScore({
            userId: apiUserData.userId,
            sessionId: apiUserData.sessionId,
            organizationId: apiUserData.organizationId,
            roles: apiUserData.roles || apiUserData.user_type || apiUserData.role || "ORG_USER",
            name: apiUserData.name || "",
            email: apiUserData.email || "",
            points: nextScore,
            time: formattedTime,
          });

          console.log("insertScore success", { level: currentLevel, points: nextScore });
          await updateScoreWithRole(apiUserData, nextScore, formattedTime);
          didInsertScoreRef.current = true;
        } else {
          await updateScoreWithRole(apiUserData, nextScore, formattedTime);
          await updateScore({
            userId: apiUserData.userId,
            sessionId: apiUserData.sessionId,
            organizationId: apiUserData.organizationId,
            points: nextScore,
            time: formattedTime,
          });
          console.log("updateScore success", { level: currentLevel, points: nextScore });
        }
        cumulativeTimeSecondsRef.current = cumulativeSeconds;
        lastReportedLevelRef.current = currentLevel;
      } catch (error) {
        console.error("Failed to sync level score:", error);
      }
    };

    syncLevelScore();
  }, [currentLevel, flushActiveTime, formatSecondsToTime, getApiUserData, isComplete, updateScoreWithRole]);

  const leaderboardIdentity = useMemo(() => {
    const user = getApiUserData() || {};
    const name = String(user.name ?? "").trim();
    const email = String(user.email ?? "").trim();
    const empId = String(user.empId ?? user.employeeId ?? user.emp_id ?? "").trim();
    return (
      name ||
      email ||
      empId ||
      "Unknown Player"
    );
  }, [getApiUserData]);

  const handleNextLevel = useCallback(async () => {
    setIsNextLevelLoading(true);
    try {
      await nextLevel();
    } finally {
      setIsNextLevelLoading(false);
    }
  }, [nextLevel]);

  const loadLeaderboard = useCallback(async () => {
    const user = getApiUserData();
    if (!user?.userId || !user?.sessionId || !user?.organizationId) {
      return { rows: [], currentUserRank: null };
    }

    try {
      const result = await getLeaderboard({
        userId: user.userId,
        sessionId: user.sessionId,
        organizationId: user.organizationId,
        limit: 100,
      });

      const rows = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.leaderboard)
          ? result.leaderboard
          : Array.isArray(result)
            ? result
            : [];

      const mappedRows = rows.map((row, index) => ({
        rank: Number(row.rank ?? row.position ?? index + 1),
        name: String(row.name ?? "").trim() ||
          String(row.email ?? "").trim() ||
          String(row.empId ?? row.employeeId ?? row.emp_id ?? "").trim() ||
          "Unknown Player",
        level: Number(row.level ?? row.points ?? row.score ?? 0),
        time: String(row.time ?? "00:00"),
        isCurrentUser:
          String(row.userId ?? row.user_id ?? "") === String(user.userId) ||
          String(row.email ?? "").toLowerCase() === String(user.email ?? "").toLowerCase(),
      }));

      const currentUserRow = mappedRows.find((row) => row.isCurrentUser);
      return {
        rows: mappedRows.slice(0, 5),
        currentUserRank: currentUserRow?.rank ?? null,
      };
    } catch (error) {
      console.error("Failed to load leaderboard:", error);
      return { rows: [], currentUserRank: null };
    }
  }, [getApiUserData]);
  
  return (
    <div className="game" aria-busy={isNextLevelLoading}>
      {isNextLevelLoading && (
        <div className="game__next-level-loader" role="status" aria-live="polite">
          <img src={loaderIcon} alt="" decoding="async" />
          <span className="game__next-level-loader-label">Loading next level…</span>
        </div>
      )}
      <div className="game__main">
        <UnblockGrid prisonerRow={prisonerRow} isComplete={isComplete}>
          {regularBlocks.map(block => (
            <Block
              key={block.id}
              block={block}
              onMove={moveBlockToPosition}
              getMoveRange={getBlockMoveRange}
              isComplete={isComplete}
            />
          ))}
          
          {prisonerBlock && (
            <PrisonerBlock
              key={prisonerBlock.id}
              block={prisonerBlock}
              onMove={moveBlockToPosition}
              getMoveRange={getBlockMoveRange}
              isComplete={isComplete}
            />
          )}
        </UnblockGrid>
        
        {showLevelCompleteOverlay && (
          <LevelComplete
            moveCount={moveCount}
            optimalMoves={optimalMoves}
            onNextLevel={handleNextLevel}
          />
        )}
      </div>
      
      <GameControls
        onRestart={restart}
        onUndo={undo}
        canUndo={canUndo}
        moveCount={moveCount}
        currentLevel={currentLevel}
        optimalMoves={optimalMoves}
        leaderboardIdentity={leaderboardIdentity}
        levelStartTimestamp={levelStartTimestamp}
        formatElapsedTime={formatElapsedTime}
        loadLeaderboard={loadLeaderboard}
      />
    </div>
  );
}

export default Game;
